import { useState, useEffect } from 'react';
import { Loader2, UserPlus, ArrowRightLeft, Search, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  owner: 'bg-amber-50 text-amber-700 border-amber-200',
  admin: 'bg-blue-50 text-blue-700 border-blue-200',
  manager: 'bg-purple-50 text-purple-700 border-purple-200',
  editor: 'bg-teal-50 text-teal-700 border-teal-200',
  viewer: 'bg-slate-50 text-slate-600 border-slate-200',
};

const ROLE_HIERARCHY = { viewer: 1, editor: 2, manager: 3, admin: 4, owner: 5 };
const ALL_ROLES = ['viewer', 'editor', 'manager', 'admin'];

const UsersTab = ({ headers, myRole }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const isOwner = myRole === 'owner';
  const myLevel = ROLE_HIERARCHY[myRole] || 0;

  // Roles this user can assign (up to their own level)
  const assignableRoles = ALL_ROLES.filter(r => ROLE_HIERARCHY[r] <= myLevel);

  // Can this user manage the target?
  const canManage = (targetRole) => {
    if (myRole === 'owner') return targetRole !== 'owner';
    return ROLE_HIERARCHY[targetRole] < myLevel;
  };

  const fetchTeam = async () => {
    try {
      const res = await fetch(`${API}/api/admin/team`, { headers, credentials: 'include' });
      if (res.ok) { const data = await res.json(); setMembers(data.members || []); }
    } catch (e) { console.error('Failed to fetch team:', e); } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => { fetchTeam(); }, []);

  const changeRole = async (email, role) => {
    const res = await fetch(`${API}/api/admin/team/${encodeURIComponent(email)}/role`, {
      method: 'PUT', headers, credentials: 'include', body: JSON.stringify({ role }),
    });
    if (res.ok) fetchTeam();
    else { const d = await res.json(); alert(d.detail || 'Failed to change role'); }
  };

  const removeUser = async (email) => {
    if (!window.confirm(`Remove ${email} from the admin team?`)) return;
    const res = await fetch(`${API}/api/admin/team/${encodeURIComponent(email)}`, {
      method: 'DELETE', headers, credentials: 'include',
    });
    if (res.ok) fetchTeam();
    else { const d = await res.json(); alert(d.detail || 'Failed to remove'); }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-taxera-blue" />;

  const active = members.filter(m => m.role !== 'pending');

  return (
    <div data-testid="admin-roles-access">
      {/* Header with actions */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-lg font-bold text-taxera-charcoal" data-testid="roles-access-title">Roles & Access</h3>
          <p className="text-xs text-slate-400 mt-0.5">{active.length} team member{active.length !== 1 ? 's' : ''} with admin access</p>
        </div>
        <div className="flex items-center gap-2">
          {isOwner && (
            <button
              onClick={() => setShowTransferModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs font-semibold hover:bg-amber-100 transition-colors"
              data-testid="transfer-ownership-btn"
            >
              <ArrowRightLeft className="w-3.5 h-3.5" /> Transfer Ownership
            </button>
          )}
          {myLevel >= ROLE_HIERARCHY.editor && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 taxera-gradient text-white rounded-lg text-xs font-semibold hover:shadow-lg transition-all"
              data-testid="add-member-btn"
            >
              <UserPlus className="w-3.5 h-3.5" /> Add Member
            </button>
          )}
        </div>
      </div>

      {/* Members table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 bg-slate-50">
              <th className="text-left px-5 py-3 text-2xs font-bold text-slate-400 uppercase">Name</th>
              <th className="text-left px-5 py-3 text-2xs font-bold text-slate-400 uppercase">Email</th>
              <th className="text-left px-5 py-3 text-2xs font-bold text-slate-400 uppercase">Role</th>
              <th className="text-left px-5 py-3 text-2xs font-bold text-slate-400 uppercase">Actions</th>
            </tr></thead>
            <tbody>
              {active.map((m) => (
                <tr key={m.email} className="border-b border-slate-50" data-testid={`team-${m.email}`}>
                  <td className="px-5 py-3 font-semibold text-taxera-charcoal">{m.name || '—'}</td>
                  <td className="px-5 py-3 text-slate-500">{m.email}</td>
                  <td className="px-5 py-3">
                    {canManage(m.role) && assignableRoles.length > 0 ? (
                      <select
                        value={m.role}
                        onChange={(e) => changeRole(m.email, e.target.value)}
                        className={`text-xs px-2.5 py-1 rounded-full font-semibold border cursor-pointer focus:outline-none ${ROLE_COLORS[m.role] || ROLE_COLORS.viewer}`}
                        data-testid={`role-${m.email}`}
                      >
                        {assignableRoles.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                      </select>
                    ) : (
                      <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border ${ROLE_COLORS[m.role] || ROLE_COLORS.viewer}`}>
                        {m.role?.charAt(0).toUpperCase() + m.role?.slice(1)}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {canManage(m.role) && (
                      <button
                        onClick={() => removeUser(m.email)}
                        className="text-xs text-red-400 hover:text-red-600 transition-colors"
                        data-testid={`remove-${m.email}`}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role legend */}
      <div className="mt-4 p-4 bg-slate-50 rounded-xl">
        <p className="text-2xs font-bold text-slate-400 uppercase mb-2">Role Permissions</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs text-slate-500">
          <div><span className="font-semibold text-amber-700">Owner</span> — Full access + ownership transfer</div>
          <div><span className="font-semibold text-blue-700">Admin</span> — Full access, manage all roles</div>
          <div><span className="font-semibold text-purple-700">Manager</span> — Leads, contacts, manage below</div>
          <div><span className="font-semibold text-teal-700">Editor</span> — Blog, news, videos, social</div>
          <div><span className="font-semibold text-slate-600">Viewer</span> — Read-only dashboard access</div>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <AddMemberModal
          headers={headers}
          assignableRoles={assignableRoles}
          onClose={() => setShowAddModal(false)}
          onAdded={() => { setShowAddModal(false); fetchTeam(); }}
        />
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <TransferOwnershipModal
          headers={headers}
          members={active.filter(m => m.role !== 'owner')}
          onClose={() => setShowTransferModal(false)}
          onTransferred={() => { setShowTransferModal(false); window.location.reload(); }}
        />
      )}
    </div>
  );
};

/* ─── Add Member Modal ─── */
const AddMemberModal = ({ headers, assignableRoles, onClose, onAdded }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState(assignableRoles[0] || 'viewer');
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const searchUsers = async () => {
    if (search.length < 2) return;
    setSearching(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/users?limit=100`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const filtered = (data.users || []).filter(u =>
          u.email?.toLowerCase().includes(search.toLowerCase()) ||
          u.name?.toLowerCase().includes(search.toLowerCase())
        ).slice(0, 10);
        setResults(filtered);
        if (filtered.length === 0) setError('No matching users found');
      }
    } catch { setError('Search failed'); }
    finally { setSearching(false); }
  };

  const addMember = async () => {
    if (!selectedEmail) return;
    setAdding(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/team/add`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ email: selectedEmail, role: selectedRole }),
      });
      if (res.ok) { onAdded(); }
      else { const d = await res.json(); setError(d.detail || 'Failed to add member'); }
    } catch { setError('Connection error'); }
    finally { setAdding(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="add-member-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-taxera-charcoal">Add Team Member</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-4">
          {/* Search */}
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Search User Database</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && searchUsers()}
                  placeholder="Search by name or email..."
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-taxera-teal"
                  data-testid="search-user-input"
                />
              </div>
              <button onClick={searchUsers} disabled={searching} className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-colors" data-testid="search-user-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </button>
            </div>
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {results.map(u => (
                <button
                  key={u.email}
                  onClick={() => setSelectedEmail(u.email)}
                  className={`w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedEmail === u.email ? 'bg-taxera-blue/5 border-l-2 border-l-taxera-blue' : ''}`}
                  data-testid={`user-result-${u.email}`}
                >
                  <div className="font-semibold text-taxera-charcoal">{u.name || u.email}</div>
                  <div className="text-xs text-slate-400">{u.email}</div>
                </button>
              ))}
            </div>
          )}

          {/* Selected + Role */}
          {selectedEmail && (
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-400 mb-1">Selected user</p>
              <p className="text-sm font-semibold text-taxera-charcoal mb-2">{selectedEmail}</p>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Assign Role</label>
              <select
                value={selectedRole}
                onChange={e => setSelectedRole(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-taxera-teal"
                data-testid="assign-role-select"
              >
                {assignableRoles.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
              </select>
            </div>
          )}

          {error && <p className="text-xs text-red-500" data-testid="add-member-error">{error}</p>}

          <button
            onClick={addMember}
            disabled={!selectedEmail || adding}
            className="w-full py-2.5 taxera-gradient text-white rounded-lg text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="confirm-add-member-btn"
          >
            {adding ? 'Adding...' : `Add as ${selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}`}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Transfer Ownership Modal ─── */
const TransferOwnershipModal = ({ headers, members, onClose, onTransferred }) => {
  const [selectedEmail, setSelectedEmail] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [error, setError] = useState('');

  const doTransfer = async () => {
    if (confirmText !== 'TRANSFER') return;
    setTransferring(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/team/transfer-ownership`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ email: selectedEmail }),
      });
      if (res.ok) { onTransferred(); }
      else { const d = await res.json(); setError(d.detail || 'Transfer failed'); }
    } catch { setError('Connection error'); }
    finally { setTransferring(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="transfer-ownership-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-taxera-charcoal">Transfer Ownership</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <p className="text-xs text-amber-800 font-semibold">This action is irreversible. You will become an Admin and the selected person will become the Owner.</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Transfer to</label>
            <select
              value={selectedEmail}
              onChange={e => setSelectedEmail(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-taxera-teal"
              data-testid="transfer-target-select"
            >
              <option value="">Select a team member...</option>
              {members.map(m => (
                <option key={m.email} value={m.email}>{m.name || m.email} ({m.role})</option>
              ))}
            </select>
          </div>

          {selectedEmail && (
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Type TRANSFER to confirm</label>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="TRANSFER"
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                data-testid="transfer-confirm-input"
              />
            </div>
          )}

          {error && <p className="text-xs text-red-500">{error}</p>}

          <button
            onClick={doTransfer}
            disabled={!selectedEmail || confirmText !== 'TRANSFER' || transferring}
            className="w-full py-2.5 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="confirm-transfer-btn"
          >
            {transferring ? 'Transferring...' : 'Transfer Ownership'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsersTab;
